import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import hapi, { API, hError } from "@/lib/health";
import { useHealth } from "@/context/HealthAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import HospitalsTab from "@/pages/health/HospitalsTab";
import {
  ShieldCheck, LogOut, FolderHeart, Pill, StickyNote, Users, UserPlus, Upload, Eye, Trash2,
  Loader2, Plus, ChevronDown, HospitalIcon, FileText, User,
} from "lucide-react";
import { toast } from "sonner";

const DOC_CATS = [
  { key: "reports", label: "Lab Reports" }, { key: "prescriptions", label: "Prescriptions" },
  { key: "bills", label: "Bills" }, { key: "imaging", label: "Imaging / Scans" }, { key: "other", label: "Other" },
];
const catLabel = (k) => (DOC_CATS.find((c) => c.key === k) || { label: "Other" }).label;
const TABS = [
  { key: "documents", label: "Documents", icon: FolderHeart },
  { key: "medications", label: "Medications", icon: Pill },
  { key: "notes", label: "Notes", icon: StickyNote },
  { key: "hospitals", label: "Hospitals", icon: HospitalIcon },
  { key: "family", label: "Family", icon: Users },
  { key: "profile", label: "Profile", icon: User },
];

export default function HealthDashboard() {
  const { state, logout, switchProfile, loadMe } = useHealth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("documents");
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const acc = state?.account;
  const doLogout = async () => { navigate("/health"); await logout(); };
  const doSwitch = async (id) => { setSwitcherOpen(false); await switchProfile(id); toast.success("Switched profile"); };

  if (!acc) return <div className="h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  const members = state.group || [];

  return (
    <div className="min-h-screen bg-emerald-50/40" data-testid="health-dashboard">
      <header className="bg-white border-b border-emerald-100 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-bold text-emerald-700"><ShieldCheck className="h-6 w-6" /> Mediva Health</div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button onClick={() => state.can_switch && setSwitcherOpen((o) => !o)} className={`flex items-center gap-2 rounded-full border border-slate-200 pl-1 pr-2.5 py-1 ${state.can_switch ? "hover:bg-slate-50" : ""}`} data-testid="profile-switcher">
                <span className="h-7 w-7 rounded-full bg-emerald-600 text-white grid place-items-center text-xs font-semibold">{(acc.name || "?").slice(0, 1).toUpperCase()}</span>
                <span className="text-sm font-medium text-slate-700 max-w-[8rem] truncate">{acc.name}</span>
                {state.can_switch && <ChevronDown className="h-4 w-4 text-slate-400" />}
              </button>
              {switcherOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1.5 z-30" data-testid="profile-switch-menu">
                  <div className="px-3 py-1 text-[11px] uppercase tracking-wider text-slate-400">Switch profile</div>
                  {members.map((m) => (
                    <button key={m.id} onClick={() => doSwitch(m.id)} className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 flex items-center gap-2" data-testid={`switch-to-${m.id}`}>
                      <span className="h-6 w-6 rounded-full bg-slate-200 grid place-items-center text-[11px] font-semibold">{(m.name || "?").slice(0, 1).toUpperCase()}</span>
                      <span className="flex-1 truncate">{m.name}</span>
                      {m.id === acc.id && <span className="text-[10px] text-emerald-600">current</span>}
                      {m.relation && m.relation !== "self" && m.id !== acc.id && <span className="text-[10px] text-slate-400">{m.relation}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={doLogout} data-testid="logout-btn" title="Log out"><LogOut className="h-5 w-5 text-slate-500" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-5 mb-6 flex items-center gap-3" data-testid="connect-hospital-banner">
          <HospitalIcon className="h-6 w-6 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold">Connect your hospitals</div>
            <div className="text-sm text-emerald-50/90">Link the hospitals you visit to pull your records in — and share your vault with them.</div>
          </div>
          <Button size="sm" onClick={() => setTab("hospitals")} className="bg-white text-emerald-700 hover:bg-emerald-50" data-testid="banner-connect-btn">Connect</Button>
        </div>

        <div className="flex gap-1.5 overflow-x-auto mb-5" data-testid="vault-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${tab === t.key ? "bg-emerald-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "documents" && <DocumentsTab accountId={acc.id} />}
        {tab === "medications" && <MedicationsTab accountId={acc.id} />}
        {tab === "notes" && <NotesTab accountId={acc.id} />}
        {tab === "hospitals" && <HospitalsTab accountId={acc.id} />}
        {tab === "family" && <FamilyTab state={state} onChange={loadMe} onSwitch={doSwitch} />}
        {tab === "profile" && <ProfileTab acc={acc} onSaved={loadMe} />}
      </main>
    </div>
  );
}

function DocumentsTab({ accountId }) {
  const [docs, setDocs] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => hapi.get("/account/vault/documents").then((r) => setDocs(r.data)).catch(() => setDocs([])), []);
  useEffect(() => { load(); }, [load, accountId]);
  const del = async (id) => { try { await hapi.delete(`/account/vault/documents/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(hError(e.response?.data?.detail)); } };
  const view = (id) => window.open(`${API}/account/vault/documents/${id}/file`, "_blank", "noopener,noreferrer");

  if (docs === null) return <Loader />;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="documents-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-slate-800">My documents</h2>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="upload-doc-btn"><Upload className="h-4 w-4 mr-1.5" /> Upload</Button>
      </div>
      {docs.length === 0 ? <Empty icon={FolderHeart} text="No documents yet. Upload a report, prescription or bill (PDF/JPG/PNG)." /> : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 border border-slate-100 rounded-lg px-3 py-2.5" data-testid={`doc-${d.id}`}>
              <FileText className="h-5 w-5 text-emerald-600 shrink-0" />
              <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{d.title}</div><div className="text-[11px] text-slate-400">{catLabel(d.category)} · {new Date(d.uploaded_at).toLocaleDateString()}</div></div>
              <Button variant="outline" size="sm" onClick={() => view(d.id)} data-testid={`doc-view-${d.id}`}><Eye className="h-3.5 w-3.5" /></Button>
              <Button variant="ghost" size="icon" onClick={() => del(d.id)} className="text-red-500" data-testid={`doc-del-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}
      <UploadDialog open={open} onOpenChange={setOpen} onDone={load} />
    </div>
  );
}

function UploadDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("reports");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    const fd = new FormData(); fd.append("file", file); fd.append("category", category); if (title) fd.append("title", title);
    setBusy(true);
    try { await hapi.post("/account/vault/documents", fd); toast.success("Uploaded"); onOpenChange(false); setFile(null); setTitle(""); onDone(); }
    catch (e) { toast.error(hError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upload-doc-dialog">
        <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Title (optional)</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. CBC report — Jan 2026" data-testid="upload-title" /></div>
          <div className="space-y-1.5"><Label>Category</Label>
            <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="upload-category">
              {DOC_CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <label className="flex items-center justify-center gap-2 border border-dashed border-slate-300 rounded-md py-3 text-sm text-slate-500 cursor-pointer hover:bg-slate-50" data-testid="upload-file-label">
            <Upload className="h-4 w-4" /> {file ? file.name : "Choose PDF / JPG / PNG"}
            <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="upload-file-input" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="upload-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MedicationsTab({ accountId }) {
  const [meds, setMeds] = useState(null);
  const [f, setF] = useState({ name: "", dose: "", frequency: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => hapi.get("/account/vault/medications").then((r) => setMeds(r.data)).catch(() => setMeds([])), []);
  useEffect(() => { load(); }, [load, accountId]);
  const add = async () => {
    if (!f.name.trim()) { toast.error("Medicine name is required"); return; }
    setBusy(true);
    try { await hapi.post("/account/vault/medications", f); setF({ name: "", dose: "", frequency: "", notes: "" }); load(); }
    catch (e) { toast.error(hError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (id) => { await hapi.delete(`/account/vault/medications/${id}`); load(); };
  if (meds === null) return <Loader />;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="medications-tab">
      <h2 className="font-semibold text-slate-800 mb-3">Current medications</h2>
      <div className="grid sm:grid-cols-[1.4fr_1fr_1fr_auto] gap-2 mb-4">
        <Input placeholder="Medicine name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="med-name" />
        <Input placeholder="Dose (e.g. 500mg)" value={f.dose} onChange={(e) => setF({ ...f, dose: e.target.value })} data-testid="med-dose" />
        <Input placeholder="Frequency (e.g. OD)" value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })} data-testid="med-freq" />
        <Button onClick={add} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="med-add"><Plus className="h-4 w-4" /></Button>
      </div>
      {meds.length === 0 ? <Empty icon={Pill} text="No medications added yet." /> : (
        <div className="space-y-2">
          {meds.map((m) => (
            <div key={m.id} className="flex items-center gap-3 border border-slate-100 rounded-lg px-3 py-2.5" data-testid={`med-${m.id}`}>
              <Pill className="h-4 w-4 text-emerald-600 shrink-0" />
              <div className="flex-1 min-w-0"><span className="text-sm font-medium text-slate-800">{m.name}</span><span className="text-xs text-slate-500 ml-2">{[m.dose, m.frequency].filter(Boolean).join(" · ")}</span>{m.notes && <div className="text-[11px] text-slate-400">{m.notes}</div>}</div>
              <Button variant="ghost" size="icon" onClick={() => del(m.id)} className="text-red-500" data-testid={`med-del-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NotesTab({ accountId }) {
  const [notes, setNotes] = useState(null);
  const [f, setF] = useState({ title: "", text: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => hapi.get("/account/vault/notes").then((r) => setNotes(r.data)).catch(() => setNotes([])), []);
  useEffect(() => { load(); }, [load, accountId]);
  const add = async () => {
    if (!f.text.trim()) { toast.error("Write something first"); return; }
    setBusy(true);
    try { await hapi.post("/account/vault/notes", f); setF({ title: "", text: "" }); load(); }
    catch (e) { toast.error(hError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (id) => { await hapi.delete(`/account/vault/notes/${id}`); load(); };
  if (notes === null) return <Loader />;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="notes-tab">
      <h2 className="font-semibold text-slate-800 mb-3">Health notes</h2>
      <div className="space-y-2 mb-4">
        <Input placeholder="Title (optional)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="note-title" />
        <Textarea rows={3} placeholder="e.g. Allergic to penicillin. BP usually around 120/80." value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="note-text" />
        <div className="flex justify-end"><Button onClick={add} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="note-add"><Plus className="h-4 w-4 mr-1.5" /> Add note</Button></div>
      </div>
      {notes.length === 0 ? <Empty icon={StickyNote} text="No notes yet." /> : (
        <div className="space-y-2">
          {notes.map((n) => (
            <div key={n.id} className="border border-slate-100 rounded-lg px-3 py-2.5" data-testid={`note-${n.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">{n.title && <div className="text-sm font-medium text-slate-800">{n.title}</div>}<div className="text-sm text-slate-600 whitespace-pre-wrap">{n.text}</div><div className="text-[11px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleDateString()}</div></div>
                <Button variant="ghost" size="icon" onClick={() => del(n.id)} className="text-red-500 shrink-0" data-testid={`note-del-${n.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FamilyTab({ state, onChange, onSwitch }) {
  const [open, setOpen] = useState(false);
  if (!state.is_holder) {
    return <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="family-tab"><Empty icon={Users} text="Only the primary account holder can manage family members." /></div>;
  }
  const members = (state.group || []).filter((m) => m.relation !== "self");
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="family-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-slate-800">Family profiles</h2>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-member-btn"><UserPlus className="h-4 w-4 mr-1.5" /> Add member</Button>
      </div>
      {members.length === 0 ? <Empty icon={Users} text="Add your spouse, children or parents to keep their records too." /> : (
        <div className="space-y-2">
          {members.map((m) => (
            <div key={m.id} className="flex items-center gap-3 border border-slate-100 rounded-lg px-3 py-2.5" data-testid={`member-${m.id}`}>
              <span className="h-8 w-8 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center text-sm font-semibold">{(m.name || "?").slice(0, 1).toUpperCase()}</span>
              <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{m.name}</div><div className="text-[11px] text-slate-400 capitalize">{m.relation}{m.email ? ` · ${m.email}` : ""}</div></div>
              <Button variant="outline" size="sm" onClick={() => onSwitch(m.id)} data-testid={`open-member-${m.id}`}>Open vault</Button>
            </div>
          ))}
        </div>
      )}
      <AddMemberDialog open={open} onOpenChange={setOpen} onDone={onChange} />
    </div>
  );
}

function AddMemberDialog({ open, onOpenChange, onDone }) {
  const [f, setF] = useState({ name: "", relation: "spouse", gender: "", dob: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try { await hapi.post("/account/members", f); toast.success("Family member added"); onOpenChange(false); setF({ name: "", relation: "spouse", gender: "", dob: "", email: "", password: "" }); onDone(); }
    catch (e) { toast.error(hError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-member-dialog">
        <DialogHeader><DialogTitle>Add family member</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={set("name")} data-testid="member-name" /></div>
            <div className="space-y-1.5"><Label>Relation</Label>
              <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={f.relation} onChange={set("relation")} data-testid="member-relation">
                <option value="spouse">Spouse</option><option value="child">Child</option><option value="parent">Parent</option><option value="sibling">Sibling</option><option value="family">Other</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Gender</Label>
              <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={f.gender} onChange={set("gender")} data-testid="member-gender"><option value="">—</option><option>Male</option><option>Female</option><option>Other</option></select>
            </div>
            <div className="space-y-1.5"><Label>Date of birth</Label><Input type="date" value={f.dob} onChange={set("dob")} data-testid="member-dob" /></div>
          </div>
          <div className="border-t border-slate-100 pt-3">
            <p className="text-[11px] text-slate-400 mb-2">Optional: give this member their own login so they can access their vault directly.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Login email</Label><Input type="email" value={f.email} onChange={set("email")} data-testid="member-email" /></div>
              <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={f.password} onChange={set("password")} data-testid="member-password" /></div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="member-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add member"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProfileTab({ acc, onSaved }) {
  const [f, setF] = useState({ name: acc.name || "", mobile: acc.mobile || "", gender: acc.gender || "", dob: acc.dob || "", blood_group: acc.blood_group || "", allergies: acc.allergies || "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF({ name: acc.name || "", mobile: acc.mobile || "", gender: acc.gender || "", dob: acc.dob || "", blood_group: acc.blood_group || "", allergies: acc.allergies || "" }); }, [acc]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try { await hapi.put("/account/profile", f); toast.success("Profile saved"); onSaved(); }
    catch (e) { toast.error(hError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-xl" data-testid="profile-tab">
      <h2 className="font-semibold text-slate-800 mb-4">Profile · {acc.relation === "self" ? "Account holder" : acc.relation}</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={set("name")} data-testid="profile-name" /></div>
        <div className="space-y-1.5"><Label>Mobile</Label><Input value={f.mobile} onChange={set("mobile")} data-testid="profile-mobile" /></div>
        <div className="space-y-1.5"><Label>Gender</Label>
          <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={f.gender} onChange={set("gender")} data-testid="profile-gender"><option value="">—</option><option>Male</option><option>Female</option><option>Other</option></select>
        </div>
        <div className="space-y-1.5"><Label>Date of birth</Label><Input type="date" value={f.dob} onChange={set("dob")} data-testid="profile-dob" /></div>
        <div className="space-y-1.5"><Label>Blood group</Label><Input value={f.blood_group} onChange={set("blood_group")} placeholder="e.g. O+" data-testid="profile-blood" /></div>
        <div className="space-y-1.5"><Label>Allergies</Label><Input value={f.allergies} onChange={set("allergies")} placeholder="e.g. Penicillin" data-testid="profile-allergies" /></div>
      </div>
      <div className="flex justify-end mt-4"><Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="profile-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save profile"}</Button></div>
    </div>
  );
}

const Loader = () => <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
const Empty = ({ icon: Icon, text }) => (
  <div className="text-center py-12 text-slate-400"><Icon className="h-9 w-9 mx-auto mb-2 opacity-40" /><p className="text-sm">{text}</p></div>
);
